// src\pages\IllustrationDetail.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { usePortfolio } from '../hooks/usePortfolio';
import {
  FiArrowLeft,
  FiCalendar,
  FiTag,
  FiHeart,
  FiEye,
  FiShare2,
  FiImage,
  FiChevronLeft,
  FiChevronRight,
  FiMaximize2,
  FiArrowRight,
  FiCheck,
  FiClock,
  FiZap,
  FiAward,
  FiLayers,
  FiPackage,
  FiMessageSquare,
  FiCopy,
  FiCheckCircle,
  FiTwitter,
  FiFacebook,
  FiLinkedin,
  FiSend,
  FiMail,
  FiLink,
  FiStar,
  FiShield,
  FiGrid,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Zoom } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
import PortfolioDetailSkeleton from '../components/portfolio/PortfolioDetailSkeleton';
import { motion } from 'framer-motion';
import ImagePreviewModal from '../components/portfolio/ImagePreviewModal';
import { createPortal } from 'react-dom';
import { useTheme } from '../contexts/ThemeContext';

// =============================================
// ACCENT COLOR — used ONLY for small highlights
// =============================================
const ACCENT = '#3b82f6';
const ACCENT_RGB = '59, 130, 246';

// =============================================
// CATEGORY LABELS
// =============================================
const KNOWN_CATEGORY_LABELS = {
  'logo-recreation': 'Logo Recreation',
  'vector-tracing': 'Vector Tracing',
  'clipart-tracing': 'Clipart Tracing',
  'photo-to-vector': 'Photo to Vector',
  'line-art': 'Line Art',
  'portrait-tracing': 'Portrait Tracing',
  'sticker-design': 'Sticker Design',
  'icon-vectorization': 'Icon Vectorization',
  'signature-tracing': 'Signature Tracing',
  'silhouette-tracing': 'Silhouette Tracing',
  'pattern-tracing': 'Pattern Tracing',
  'sports-logo-tracing': 'Sports & Team Logos',
  others: 'Other Works',
  branding: 'Brand Design',
  product: 'Product Visual',
};

const formatCategoryLabel = slug => {
  if (!slug) return 'Illustration';
  const key = String(slug).toLowerCase().trim();
  if (KNOWN_CATEGORY_LABELS[key]) return KNOWN_CATEGORY_LABELS[key];
  return key
    .split(/[-_]/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

// =============================================
// ANIMATIONS
// =============================================
const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06 },
  },
};

// =============================================
// FORMAT DESCRIPTION
// =============================================
const formatDescription = text => {
  if (!text) return null;
  const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim());

  return paragraphs.map((paragraph, idx) => {
    if (paragraph.trim().match(/^[-•*]\s/)) {
      const items = paragraph.split(/\n/).filter(line => line.trim());
      return (
        <ul key={idx} className="space-y-2 my-4">
          {items.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <span
                className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center mt-1"
                style={{ backgroundColor: `rgba(${ACCENT_RGB}, 0.12)` }}
              >
                <FiCheck
                  className="w-2.5 h-2.5"
                  style={{ color: ACCENT }}
                  strokeWidth={3}
                />
              </span>
              <span
                style={{ color: 'var(--text)' }}
                className="leading-relaxed"
              >
                {item.replace(/^[-•*]\s/, '')}
              </span>
            </li>
          ))}
        </ul>
      );
    }

    if (paragraph.trim().match(/^##\s/)) {
      return (
        <h3
          key={idx}
          className="text-lg font-semibold mt-6 mb-2"
          style={{ color: 'var(--text)' }}
        >
          {paragraph.replace(/^##\s/, '')}
        </h3>
      );
    }

    return (
      <p
        key={idx}
        className="leading-relaxed mb-4"
        style={{ color: 'var(--muted)' }}
      >
        {paragraph}
      </p>
    );
  });
};

// =============================================
// SHARE MODAL
// =============================================
const ShareModal = ({ isOpen, onClose, project, shareUrl }) => {
  const [copied, setCopied] = useState(false);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!isOpen || !project) return null;

  const platforms = [
    {
      id: 'twitter',
      name: 'Twitter',
      icon: FiTwitter,
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(project.title)}&url=${encodeURIComponent(shareUrl)}&via=wdhejbul`,
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: FiFacebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: FiLinkedin,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      icon: FaWhatsapp,
      url: `https://api.whatsapp.com/send?text=${encodeURIComponent(`✨ ${project.title}\n\n${shareUrl}`)}`,
    },
    {
      id: 'telegram',
      name: 'Telegram',
      icon: FiSend,
      url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(project.title)}`,
    },
    {
      id: 'email',
      name: 'Email',
      icon: FiMail,
      url: `mailto:?subject=${encodeURIComponent(`Check out: ${project.title}`)}&body=${encodeURIComponent(`I thought you might find this interesting:\n\n${project.title}\n${shareUrl}`)}`,
    },
  ];

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.18 }}
        className="relative max-w-lg w-full rounded-2xl shadow-2xl overflow-hidden"
        style={{
          backgroundColor: isDark
            ? 'rgba(20,20,20,0.98)'
            : 'rgba(255,255,255,0.98)',
          backdropFilter: 'blur(20px)',
          border: isDark
            ? '1px solid rgba(255,255,255,0.08)'
            : '1px solid rgba(0,0,0,0.06)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div
          className="p-5 border-b flex items-center justify-between"
          style={{
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                backgroundColor: isDark
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.05)',
              }}
            >
              <FiShare2 className="w-5 h-5" style={{ color: 'var(--text)' }} />
            </div>
            <div>
              <h3
                className="text-base font-semibold"
                style={{ color: 'var(--text)' }}
              >
                Share this illustration
              </h3>
              <p className="text-xs" style={{ color: 'var(--muted)' }}>
                Spread the word
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full transition-colors duration-150 hover:bg-black/5 dark:hover:bg-white/5"
          >
            <FiX className="w-5 h-5" style={{ color: 'var(--text)' }} />
          </button>
        </div>

        <div className="p-5 grid grid-cols-3 gap-2">
          {platforms.map(p => {
            const Icon = p.icon;
            return (
              <button
                key={p.id}
                onClick={() =>
                  window.open(
                    p.url,
                    '_blank',
                    'noopener,noreferrer,width=600,height=400',
                  )
                }
                className="flex flex-col items-center gap-2 p-3 rounded-xl transition-colors duration-150 hover:bg-black/5 dark:hover:bg-white/5"
                style={{
                  border: isDark
                    ? '1px solid rgba(255,255,255,0.06)'
                    : '1px solid rgba(0,0,0,0.06)',
                }}
              >
                <Icon className="w-5 h-5" style={{ color: 'var(--text)' }} />
                <span
                  className="text-xs font-medium"
                  style={{ color: 'var(--text)' }}
                >
                  {p.name}
                </span>
              </button>
            );
          })}
        </div>

        <div
          className="p-5 pt-0 border-t"
          style={{
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
          }}
        >
          <div
            className="flex items-center gap-2 p-2.5 rounded-xl border mt-4"
            style={{ borderColor: 'var(--border)' }}
          >
            <FiLink
              className="w-4 h-4 flex-shrink-0"
              style={{ color: 'var(--muted)' }}
            />
            <p
              className="text-xs truncate flex-1"
              style={{ color: 'var(--muted)' }}
            >
              {shareUrl}
            </p>
            <button
              onClick={copyToClipboard}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-150 flex items-center gap-1.5 ${
                copied
                  ? 'text-green-500 bg-green-500/10'
                  : 'hover:bg-black/5 dark:hover:bg-white/5'
              }`}
              style={{ color: copied ? '#22c55e' : 'var(--text)' }}
            >
              {copied ? (
                <>
                  <FiCheckCircle className="w-3.5 h-3.5" />
                  Copied!
                </>
              ) : (
                <>
                  <FiCopy className="w-3.5 h-3.5" />
                  Copy
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>,
    document.body,
  );
};

// =============================================
// KEY FACT (small editorial strip item)
// =============================================
const KeyFact = ({ icon: Icon, label, value }) => (
  <div
    className="flex items-center gap-3 py-3 px-1"
    style={{ borderBottom: '1px solid var(--border)' }}
  >
    <Icon className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--muted)' }} />
    <div className="flex-1 min-w-0">
      <div
        className="text-[10px] uppercase tracking-wider font-medium"
        style={{ color: 'var(--muted)' }}
      >
        {label}
      </div>
      <div
        className="text-sm font-medium truncate"
        style={{ color: 'var(--text)' }}
      >
        {value}
      </div>
    </div>
  </div>
);

// =============================================
// SERVICE FEATURE — minimal editorial style
// =============================================
const ServiceFeature = ({ icon: Icon, title, description }) => (
  <motion.div
    variants={fadeInUp}
    className="flex items-start gap-3 py-4"
    style={{ borderBottom: '1px solid var(--border)' }}
  >
    <div
      className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5"
      style={{ backgroundColor: 'var(--border-light)' }}
    >
      <Icon className="w-4 h-4" style={{ color: 'var(--muted)' }} />
    </div>
    <div className="flex-1">
      <h4 className="font-medium text-sm mb-1" style={{ color: 'var(--text)' }}>
        {title}
      </h4>
      <p className="text-xs leading-relaxed" style={{ color: 'var(--muted)' }}>
        {description}
      </p>
    </div>
  </motion.div>
);

// =============================================
// PROCESS STEP — editorial numbered list
// =============================================
const ProcessStep = ({ number, title, description }) => (
  <motion.div variants={fadeInUp} className="flex gap-4 items-start">
    <div
      className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold"
      style={{
        backgroundColor: 'var(--border)',
        color: 'var(--muted)',
      }}
    >
      {String(number).padStart(2, '0')}
    </div>
    <div className="flex-1">
      <h4 className="font-medium text-sm mb-1" style={{ color: 'var(--text)' }}>
        {title}
      </h4>
      <p className="text-xs leading-relaxed" style={{ color: 'var(--muted)' }}>
        {description}
      </p>
    </div>
  </motion.div>
);

// =============================================
// MAIN COMPONENT
// =============================================
function IllustrationDetail() {
  const { id } = useParams();
  const { getProject, trackView, trackClick, likeProject, projects } =
    usePortfolio('illustration');

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageErrors, setImageErrors] = useState({});
  const [imagesLoaded, setImagesLoaded] = useState({});
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewImageIndex, setPreviewImageIndex] = useState(0);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [isLiking, setIsLiking] = useState(false);

  const hasTrackedRef = useRef(false);

  // Load project
  useEffect(() => {
    const loadProject = async () => {
      if (!id) {
        setError('No project ID provided');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await getProject(id);
        if (data) {
          setProject(data);
          setLikesCount(data.likes || 0);
          const likedProjects = JSON.parse(
            localStorage.getItem('likedIllustrations') || '[]',
          );
          setLiked(likedProjects.includes(data.id));
        } else {
          setError('Illustration not found');
        }
      } catch (err) {
        console.error('Error loading illustration:', err);
        setError(err.message || 'Failed to load illustration');
      } finally {
        setLoading(false);
      }
    };
    loadProject();
  }, [id, getProject]);

  // Track
  useEffect(() => {
    if (project && !hasTrackedRef.current) {
      hasTrackedRef.current = true;
      trackView(project.id).catch(() => {});
      trackClick(project.id).catch(() => {});
    }
  }, [project, trackView, trackClick]);

  // Like handler
  const handleLike = async () => {
    if (isLiking || !project) return;
    const wasLiked = liked;
    const prevCount = likesCount;
    const newLiked = !liked;
    const newCount = newLiked ? likesCount + 1 : likesCount - 1;

    setLiked(newLiked);
    setLikesCount(newCount);
    setIsLiking(true);

    const likedProjects = JSON.parse(
      localStorage.getItem('likedIllustrations') || '[]',
    );
    if (newLiked) {
      if (!likedProjects.includes(project.id)) likedProjects.push(project.id);
    } else {
      const idx = likedProjects.indexOf(project.id);
      if (idx > -1) likedProjects.splice(idx, 1);
    }
    localStorage.setItem('likedIllustrations', JSON.stringify(likedProjects));

    try {
      const result = await likeProject(project.id, wasLiked);
      if (result?.likes !== undefined) setLikesCount(result.likes);
    } catch {
      setLiked(wasLiked);
      setLikesCount(prevCount);
    } finally {
      setIsLiking(false);
    }
  };

  // Copy link
  const copyShareLink = async () => {
    const url = `${window.location.origin}/illustrations/${project?.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Image helpers
  const getOptimizedImageUrl = (url, width = 1200) => {
    if (!url) return '';
    if (url.includes('cloudinary.com')) {
      return url.replace(
        '/upload/',
        `/upload/w_${width},q_auto:best,f_auto,c_limit,g_center,dpr_auto/`,
      );
    }
    return url;
  };

  const getImageAspectRatio = image => {
    if (image.ratio) return image.ratio;
    if (image.width && image.height) return image.width / image.height;
    return 1.33;
  };

  const handleImageError = index =>
    setImageErrors(prev => ({ ...prev, [index]: true }));
  const handleImageLoad = index =>
    setImagesLoaded(prev => ({ ...prev, [index]: true }));
  const handleSlideChange = swiper => setCurrentImageIndex(swiper.activeIndex);

  const formatDate = dateString => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Derived
  const images = useMemo(
    () =>
      project?.images || (project?.imageUrl ? [{ url: project.imageUrl }] : []),
    [project],
  );
  const imageCount = images.length;
  const categoryLabel = useMemo(
    () => formatCategoryLabel(project?.category),
    [project?.category],
  );

  // Service features (dynamic by category, minimal set)
  const serviceFeatures = useMemo(() => {
    if (!project) return [];
    const cat = (project.category || '').toLowerCase();

    const common = [
      {
        icon: FiPackage,
        title: 'All Source Files',
        description:
          'AI, EPS, SVG, PDF, and PNG — editable layers, print-ready.',
      },
      {
        icon: FiAward,
        title: 'Unlimited Revisions',
        description: 'Refined until you are satisfied. No limits, no fees.',
      },
      {
        icon: FiShield,
        title: 'Full Commercial Rights',
        description: 'Complete ownership transferred to you on delivery.',
      },
      {
        icon: FiClock,
        title: 'Express Available',
        description: 'Rush delivery available on request — ask anytime.',
      },
    ];

    const perCategory = {
      'logo-recreation': [
        {
          icon: FiLayers,
          title: 'Clean Vector Rebuild',
          description:
            'Hand-traced with closed paths, no auto-trace artifacts.',
        },
        {
          icon: FiCopy,
          title: 'Multiple Layouts',
          description: 'Horizontal, vertical, icon-only, and monochrome.',
        },
      ],
      'vector-tracing': [
        {
          icon: FiLayers,
          title: 'Layered Vectors',
          description: 'Named, editable layers for easy customization.',
        },
      ],
      'photo-to-vector': [
        {
          icon: FiImage,
          title: 'Stylized Conversion',
          description: 'Realistic, flat, or illustrative looks — your choice.',
        },
      ],
      'line-art': [
        {
          icon: FiZap,
          title: 'Perfect Linework',
          description: 'Uniform strokes, clean intersections, closed paths.',
        },
      ],
      'sticker-design': [
        {
          icon: FiPackage,
          title: 'Die-Cut Ready',
          description: 'Includes bleed and cut-lines — print-ready.',
        },
      ],
      'signature-tracing': [
        {
          icon: FiCheck,
          title: 'Faithful Reproduction',
          description: 'Pixel-accurate tracing keeping the natural flow.',
        },
      ],
      'sports-logo-tracing': [
        {
          icon: FiAward,
          title: 'Emblem Specialist',
          description:
            'Mascots, emblems, and shields traced with crisp detail.',
        },
      ],
      'pattern-tracing': [
        {
          icon: FiLayers,
          title: 'Seamless Tiling',
          description: 'Guaranteed repeats for textiles and packaging.',
        },
      ],
      'icon-vectorization': [
        {
          icon: FiGrid,
          title: 'Pixel-Perfect Grids',
          description: 'Aligned to consistent grids for crisp rendering.',
        },
      ],
      'portrait-tracing': [
        {
          icon: FiAward,
          title: 'Facial Detail',
          description: 'Captures expression with minimal, clean paths.',
        },
      ],
    };

    return [...(perCategory[cat] || []), ...common];
  }, [project]);

  const processSteps = useMemo(
    () => [
      {
        title: 'Brief',
        description: 'Share your file and requirements — takes minutes.',
      },
      {
        title: 'Hand-Trace',
        description: 'Every curve manually refined for precision.',
      },
      {
        title: 'Refine',
        description: 'Request tweaks until it is exactly right.',
      },
      {
        title: 'Deliver',
        description: 'All source files sent — often within 24 hours.',
      },
    ],
    [],
  );

  if (loading) return <PortfolioDetailSkeleton />;

  if (error || !project) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ backgroundColor: 'var(--bg)' }}
      >
        <div className="text-center p-8 max-w-md">
          <div
            className="w-16 h-16 mx-auto mb-5 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--border)' }}
          >
            <FiImage className="w-8 h-8" style={{ color: 'var(--muted)' }} />
          </div>
          <h2
            className="text-2xl font-bold mb-3"
            style={{ color: 'var(--text)' }}
          >
            {error || 'Illustration not found'}
          </h2>
          <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>
            The illustration you're looking for doesn't exist or has been
            removed.
          </p>
          <Link
            to="/illustrations"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm transition-colors duration-200 hover:opacity-90"
            style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
          >
            <FiArrowLeft className="w-4 h-4" />
            Back to Illustrations
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = `${window.location.origin}/illustrations/${project.id}`;
  const pageTitle = project.seoTitle || project.title;
  const pageDescription = project.seoDescription || project.description;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: project.seoTitle || project.title,
    description: project.seoDescription || project.description,
    keywords: project.tags?.join(', ') || '',
    image: project.thumbnailUrl || images[0]?.url || '',
    dateCreated: project.createdAt,
    author: { '@type': 'Person', name: 'WD Hajbul' },
    genre: 'illustration',
    about: categoryLabel,
  };

  const related = (projects || [])
    .filter(p => p.id !== project.id && p.category === project.category)
    .slice(0, 6);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={project.tags?.join(', ') || ''} />
        <link rel="canonical" href={shareUrl} />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <div
        className="min-h-screen"
        style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
      >
        {/* ============================================= */}
        {/* STICKY NAV — minimal */}
        {/* ============================================= */}
        <div
          className="sticky top-[85px] z-30 backdrop-blur-md border-b"
          style={{
            backgroundColor: 'var(--bg)',
            borderColor: 'var(--border)',
          }}
        >
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link
              to="/illustrations"
              className="flex items-center gap-2 text-sm transition-colors duration-200 hover:opacity-80"
              style={{ color: 'var(--muted)' }}
            >
              <FiArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">All Illustrations</span>
              <span className="sm:hidden">Back</span>
            </Link>

            <div className="flex items-center gap-1.5">
              {/* Like */}
              <button
                onClick={handleLike}
                disabled={isLiking}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors duration-200 ${
                  isLiking ? 'opacity-50 cursor-wait' : ''
                } ${
                  liked
                    ? 'text-red-500'
                    : 'hover:bg-black/5 dark:hover:bg-white/5'
                }`}
                style={{
                  backgroundColor: liked
                    ? 'rgba(239, 68, 68, 0.12)'
                    : 'transparent',
                  color: liked ? '#ef4444' : 'var(--text)',
                }}
                aria-label="Like"
              >
                {isLiking ? (
                  <div
                    className="w-3.5 h-3.5 border-2 border-t-transparent rounded-full animate-spin"
                    style={{ borderColor: liked ? '#ef4444' : 'var(--text)' }}
                  />
                ) : (
                  <FiHeart
                    className={`w-3.5 h-3.5 ${liked ? 'fill-current' : ''}`}
                  />
                )}
                <span className="text-xs font-medium">{likesCount}</span>
              </button>

              {/* Copy link */}
              <button
                onClick={copyShareLink}
                className={`p-1.5 rounded-lg transition-colors duration-200 ${
                  copied
                    ? 'text-green-500 bg-green-500/10'
                    : 'hover:bg-black/5 dark:hover:bg-white/5'
                }`}
                style={{ color: copied ? '#22c55e' : 'var(--muted)' }}
                aria-label="Copy link"
                title="Copy link"
              >
                {copied ? (
                  <FiCheckCircle className="w-4 h-4" />
                ) : (
                  <FiLink className="w-4 h-4" />
                )}
              </button>

              {/* Share */}
              <button
                onClick={() => setIsShareModalOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors duration-200 hover:bg-black/5 dark:hover:bg-white/5 text-sm"
                style={{ color: 'var(--muted)' }}
                aria-label="Share"
              >
                <FiShare2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </button>
            </div>
          </div>
        </div>

        {/* ============================================= */}
        {/* MAIN */}
        {/* ============================================= */}
        <div className="max-w-6xl mx-auto px-4 pt-30 pb-16">
          {/* ---------- HEADLINE BLOCK ---------- */}
          <motion.header
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="mb-10 max-w-3xl"
          >
            {/* Eyebrow */}
            <div
              className="flex items-center gap-2 mb-4 text-xs uppercase tracking-wider"
              style={{ color: 'var(--muted)' }}
            >
              <span
                className="w-6 h-px"
                style={{ backgroundColor: 'var(--muted)' }}
              />
              <span>Illustration · {categoryLabel}</span>
            </div>

            {/* Title */}
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] mb-4"
              style={{ color: 'var(--text)' }}
            >
              {project.title}
            </h1>

            {/* Lede — short description line under title */}
            {project.description && (
              <p
                className="text-base sm:text-lg leading-relaxed max-w-2xl"
                style={{ color: 'var(--muted)' }}
              >
                {project.description.split('\n')[0].slice(0, 200)}
                {project.description.split('\n')[0].length > 200 ? '…' : ''}
              </p>
            )}
          </motion.header>

          {/* ---------- LAYOUT GRID ---------- */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* LEFT: IMAGES (7 cols) */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="lg:col-span-7"
            >
              <div
                className="relative w-full rounded-2xl overflow-hidden"
                style={{
                  backgroundColor: 'rgba(0,0,0,0.02)',
                  border: '1px solid var(--border)',
                }}
              >
                <div className="relative aspect-video max-h-[620px]">
                  {imageCount === 0 ? (
                    <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center">
                      <FiImage
                        className="w-12 h-12 mb-3 opacity-30"
                        style={{ color: 'var(--text)' }}
                      />
                      <p className="text-sm" style={{ color: 'var(--muted)' }}>
                        No images available
                      </p>
                    </div>
                  ) : (
                    <>
                      <Swiper
                        modules={[Navigation, Pagination, Zoom]}
                        navigation={{
                          nextEl: '.ill-next',
                          prevEl: '.ill-prev',
                        }}
                        pagination={{ clickable: true, dynamicBullets: true }}
                        zoom={{ maxRatio: 3, minRatio: 1 }}
                        loop={imageCount > 1}
                        onSlideChange={handleSlideChange}
                        className="ill-detail-swiper"
                        style={{ width: '100%', height: '100%' }}
                      >
                        {images.map((image, index) => {
                          const imageUrl = image.url || image;
                          const isError = imageErrors[index];
                          const isLoaded = imagesLoaded[index];
                          const ratio = getImageAspectRatio(image);
                          const isPortrait = ratio < 0.9;
                          return (
                            <SwiperSlide key={index}>
                              <div
                                className="w-full h-full flex items-center justify-center bg-black/5 dark:bg-black/20 cursor-zoom-in"
                                onClick={() => {
                                  setPreviewImageIndex(index);
                                  setIsPreviewModalOpen(true);
                                }}
                              >
                                {!isLoaded && !isError && (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div
                                      className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin"
                                      style={{ borderColor: 'var(--muted)' }}
                                    />
                                  </div>
                                )}
                                {isError ? (
                                  <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center">
                                    <FiImage
                                      className="w-12 h-12 mb-3 opacity-30"
                                      style={{ color: 'var(--text)' }}
                                    />
                                    <p
                                      className="text-sm"
                                      style={{ color: 'var(--muted)' }}
                                    >
                                      Unable to load image
                                    </p>
                                  </div>
                                ) : (
                                  <img
                                    src={getOptimizedImageUrl(imageUrl, 1200)}
                                    alt={`${project.title} - Image ${index + 1}`}
                                    className={`transition-opacity duration-500 ${
                                      isLoaded ? 'opacity-100' : 'opacity-0'
                                    }`}
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: '100%',
                                      width: isPortrait ? 'auto' : '100%',
                                      height: isPortrait ? '100%' : 'auto',
                                      objectFit: 'contain',
                                      display: 'block',
                                    }}
                                    onLoad={() => handleImageLoad(index)}
                                    onError={() => handleImageError(index)}
                                    loading="lazy"
                                  />
                                )}
                              </div>
                            </SwiperSlide>
                          );
                        })}
                      </Swiper>

                      {imageCount > 1 && (
                        <>
                          <button
                            className="ill-prev absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full backdrop-blur-md flex items-center justify-center transition-colors duration-200 hover:bg-black/70"
                            style={{
                              backgroundColor: 'rgba(0,0,0,0.45)',
                              color: 'white',
                              border: '1px solid rgba(255,255,255,0.1)',
                            }}
                            aria-label="Previous"
                          >
                            <FiChevronLeft className="w-4 h-4" />
                          </button>
                          <button
                            className="ill-next absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full backdrop-blur-md flex items-center justify-center transition-colors duration-200 hover:bg-black/70"
                            style={{
                              backgroundColor: 'rgba(0,0,0,0.45)',
                              color: 'white',
                              border: '1px solid rgba(255,255,255,0.1)',
                            }}
                            aria-label="Next"
                          >
                            <FiChevronRight className="w-4 h-4" />
                          </button>
                          <div
                            className="absolute top-3 right-3 px-2.5 py-1 rounded-full backdrop-blur-md text-xs font-medium z-10"
                            style={{
                              backgroundColor: 'rgba(0,0,0,0.55)',
                              color: 'white',
                              border: '1px solid rgba(255,255,255,0.1)',
                            }}
                          >
                            {currentImageIndex + 1} / {imageCount}
                          </div>
                        </>
                      )}

                      <button
                        onClick={() => {
                          setPreviewImageIndex(currentImageIndex);
                          setIsPreviewModalOpen(true);
                        }}
                        className="absolute bottom-3 right-3 z-10 w-9 h-9 rounded-full backdrop-blur-md flex items-center justify-center transition-colors duration-200 hover:bg-black/70"
                        style={{
                          backgroundColor: 'rgba(0,0,0,0.45)',
                          color: 'white',
                          border: '1px solid rgba(255,255,255,0.1)',
                        }}
                        aria-label="Fullscreen"
                      >
                        <FiMaximize2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* ---------- KEY FACTS STRIP (below image) ---------- */}
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-x-6">
                <KeyFact icon={FiClock} label="Delivery" value="24 hours" />
                <KeyFact icon={FiAward} label="Revisions" value="Unlimited" />
                <KeyFact icon={FiShield} label="Rights" value="Commercial" />
              </div>
            </motion.div>

            {/* RIGHT: STICKY INFO (5 cols) */}
            <motion.aside
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="lg:col-span-5"
            >
              <div className="lg:sticky lg:top-32">
                {/* Primary CTA block */}
                <div
                  className="rounded-2xl p-6 mb-6"
                  style={{
                    backgroundColor: 'var(--border-light)',
                    border: '1px solid var(--border)',
                  }}
                >
                  <div
                    className="text-xs uppercase tracking-wider mb-2"
                    style={{ color: 'var(--muted)' }}
                  >
                    Start here
                  </div>
                  <h2
                    className="text-xl font-bold mb-2 leading-tight"
                    style={{ color: 'var(--text)' }}
                  >
                    Get this illustration for your project
                  </h2>
                  <p
                    className="text-sm mb-5 leading-relaxed"
                    style={{ color: 'var(--muted)' }}
                  >
                    Send your file and I'll reply within a few hours with a
                    quote. Most projects are delivered within 24 hours.
                  </p>

                  <div className="space-y-2">
                    <Link
                      to="/hire"
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-200 hover:opacity-90"
                      style={{
                        backgroundColor: 'var(--text)',
                        color: 'var(--bg)',
                      }}
                    >
                      <FiMessageSquare className="w-4 h-4" />
                      Request a Quote
                    </Link>
                    <a
                      href="https://www.fiverr.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-200 hover:bg-black/5 dark:hover:bg-white/5"
                      style={{
                        backgroundColor: 'var(--bg)',
                        color: 'var(--text)',
                        border: '1px solid var(--border)',
                      }}
                    >
                      Order on Fiverr
                      <FiArrowRight className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>

                {/* Meta (small editorial list) */}
                <div className="mb-6">
                  <div
                    className="text-xs uppercase tracking-wider mb-3"
                    style={{ color: 'var(--muted)' }}
                  >
                    Details
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between gap-4">
                      <span style={{ color: 'var(--muted)' }}>Category</span>
                      <span
                        className="text-right"
                        style={{ color: 'var(--text)' }}
                      >
                        {categoryLabel}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span style={{ color: 'var(--muted)' }}>Published</span>
                      <span
                        className="text-right"
                        style={{ color: 'var(--text)' }}
                      >
                        {formatDate(project.createdAt)}
                      </span>
                    </div>
                    {imageCount > 0 && (
                      <div className="flex justify-between gap-4">
                        <span style={{ color: 'var(--muted)' }}>Images</span>
                        <span
                          className="text-right"
                          style={{ color: 'var(--text)' }}
                        >
                          {imageCount}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Tags */}
                {project.tags && project.tags.length > 0 && (
                  <div>
                    <div
                      className="text-xs uppercase tracking-wider mb-3"
                      style={{ color: 'var(--muted)' }}
                    >
                      Tags
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {project.tags.slice(0, 10).map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2.5 py-1 rounded-full text-[11px]"
                          style={{
                            backgroundColor: 'var(--border)',
                            color: 'var(--muted)',
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.aside>
          </div>

          {/* ---------- FULL DESCRIPTION ---------- */}
          {project.description &&
            project.description.split('\n').length > 1 && (
              <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-50px' }}
                variants={fadeInUp}
                className="mt-16 pt-10 grid grid-cols-1 lg:grid-cols-12 gap-10"
                style={{ borderTop: '1px solid var(--border)' }}
              >
                <div className="lg:col-span-3">
                  <div
                    className="text-xs uppercase tracking-wider mb-2"
                    style={{ color: 'var(--muted)' }}
                  >
                    About
                  </div>
                  <h2
                    className="text-xl font-bold leading-tight"
                    style={{ color: 'var(--text)' }}
                  >
                    The details
                  </h2>
                </div>
                <div className="lg:col-span-9 max-w-3xl">
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    {formatDescription(project.description)}
                  </div>
                </div>
              </motion.section>
            )}

          {/* ---------- DELIVERABLES ---------- */}
          {serviceFeatures.length > 0 && (
            <motion.section
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              variants={staggerContainer}
              className="mt-16 pt-10 grid grid-cols-1 lg:grid-cols-12 gap-10"
              style={{ borderTop: '1px solid var(--border)' }}
            >
              <div className="lg:col-span-3">
                <motion.div variants={fadeInUp} className="lg:sticky lg:top-32">
                  <div
                    className="text-xs uppercase tracking-wider mb-2"
                    style={{ color: 'var(--muted)' }}
                  >
                    Deliverables
                  </div>
                  <h2
                    className="text-xl font-bold leading-tight mb-3"
                    style={{ color: 'var(--text)' }}
                  >
                    What you receive
                  </h2>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: 'var(--muted)' }}
                  >
                    Every project ships complete — no extras, no surprises.
                  </p>
                </motion.div>
              </div>
              <div className="lg:col-span-9">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
                  {serviceFeatures.map((feature, idx) => (
                    <ServiceFeature key={idx} {...feature} />
                  ))}
                </div>
              </div>
            </motion.section>
          )}

          {/* ---------- PROCESS ---------- */}
          <motion.section
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            variants={staggerContainer}
            className="mt-16 pt-10 grid grid-cols-1 lg:grid-cols-12 gap-10"
            style={{ borderTop: '1px solid var(--border)' }}
          >
            <div className="lg:col-span-3">
              <motion.div variants={fadeInUp} className="lg:sticky lg:top-32">
                <div
                  className="text-xs uppercase tracking-wider mb-2"
                  style={{ color: 'var(--muted)' }}
                >
                  Process
                </div>
                <h2
                  className="text-xl font-bold leading-tight"
                  style={{ color: 'var(--text)' }}
                >
                  How we work
                </h2>
              </motion.div>
            </div>
            <div className="lg:col-span-9">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {processSteps.map((step, idx) => (
                  <ProcessStep
                    key={idx}
                    number={idx + 1}
                    title={step.title}
                    description={step.description}
                  />
                ))}
              </div>
            </div>
          </motion.section>

          {/* ---------- RELATED ---------- */}
          {related.length > 0 && (
            <motion.section
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              variants={staggerContainer}
              className="mt-16 pt-10"
              style={{ borderTop: '1px solid var(--border)' }}
            >
              <div className="flex items-baseline justify-between mb-6">
                <motion.div variants={fadeInUp}>
                  <div
                    className="text-xs uppercase tracking-wider mb-1"
                    style={{ color: 'var(--muted)' }}
                  >
                    More work
                  </div>
                  <h2
                    className="text-xl font-bold"
                    style={{ color: 'var(--text)' }}
                  >
                    Similar illustrations
                  </h2>
                </motion.div>
                <Link
                  to="/illustrations"
                  className="text-xs transition-colors duration-200 hover:opacity-80 flex items-center gap-1"
                  style={{ color: 'var(--muted)' }}
                >
                  View all <FiArrowRight className="w-3 h-3" />
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {related.map(item => (
                  <motion.div
                    key={item.id}
                    variants={fadeInUp}
                    className="group cursor-pointer rounded-xl overflow-hidden transition-shadow duration-200 hover:shadow-md"
                    style={{ border: '1px solid var(--border)' }}
                  >
                    <Link to={`/illustrations/${item.id}`}>
                      <div className="aspect-video overflow-hidden bg-gray-100 dark:bg-gray-800">
                        <img
                          src={item.thumbnailUrl || item.images?.[0]?.url || ''}
                          alt={item.title}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3">
                        <p
                          className="text-xs font-medium truncate mb-1"
                          style={{ color: 'var(--text)' }}
                        >
                          {item.title}
                        </p>
                        <p
                          className="text-[10px]"
                          style={{ color: 'var(--muted)' }}
                        >
                          {formatCategoryLabel(item.category)}
                        </p>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.section>
          )}

          {/* ---------- CLOSING CTA ---------- */}
          <motion.section
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            variants={fadeInUp}
            className="mt-16 pt-10"
            style={{ borderTop: '1px solid var(--border)' }}
          >
            <div className="max-w-2xl">
              <h2
                className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight mb-3"
                style={{ color: 'var(--text)' }}
              >
                Have an image that needs tracing?
              </h2>
              <p
                className="text-sm mb-6 leading-relaxed"
                style={{ color: 'var(--muted)' }}
              >
                Send your file, get a quote in minutes, and receive
                pixel-perfect vectors — usually within 24 hours.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to="/hire"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors duration-200 hover:opacity-90"
                  style={{
                    backgroundColor: 'var(--text)',
                    color: 'var(--bg)',
                  }}
                >
                  Start a Project
                  <FiArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/illustrations"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors duration-200 hover:bg-black/5 dark:hover:bg-white/5"
                  style={{
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                  }}
                >
                  Browse More Work
                </Link>
              </div>
            </div>
          </motion.section>
        </div>

        {/* Image Preview Modal */}
        <ImagePreviewModal
          isOpen={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          images={images}
          initialIndex={previewImageIndex}
          project={project}
          onLike={handleLike}
          likesCount={likesCount}
          isLiked={liked}
          isLiking={isLiking}
        />

        {/* Share Modal */}
        {isShareModalOpen && (
          <ShareModal
            isOpen={isShareModalOpen}
            onClose={() => setIsShareModalOpen(false)}
            project={project}
            shareUrl={shareUrl}
          />
        )}
      </div>

      {/* Swiper styles */}
      <style>{`
        .ill-detail-swiper {
          width: 100%;
          height: 100%;
        }
        .ill-detail-swiper .swiper-button-prev,
        .ill-detail-swiper .swiper-button-next {
          display: none;
        }
        .ill-detail-swiper .swiper-pagination {
          position: absolute;
          bottom: 8px;
        }
        .ill-detail-swiper .swiper-pagination-bullet {
          background: white;
          opacity: 0.5;
          width: 6px;
          height: 6px;
        }
        .ill-detail-swiper .swiper-pagination-bullet-active {
          opacity: 1;
          width: 20px;
          border-radius: 4px;
          background: white;
        }
        .ill-detail-swiper .swiper-zoom-container {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .ill-detail-swiper .swiper-slide {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          width: 100%;
        }
      `}</style>
    </>
  );
}

export default IllustrationDetail;
